import { AppConfig } from "../config/appConfig";
import { ErrorProperties } from "../types/system";

export class ErrorProvider {
	private static readonly _defaultErrorMessage: string = AppConfig.defaultErrorProviderMessage ?? "We encountered an error while processing your request. Please try again.";
	private static readonly _messageContentIdentifier: string = "DISPLAYMESSAGE";
	private static readonly _tag = { start: `<${this._messageContentIdentifier}>`, end: `</${this._messageContentIdentifier}>` }

	/**
	 * Attempts to extract messages from known error formats, returning a default message if unsuccessful.
	 * @param error The error object.
	 * @returns The extracted, or default, error message.
	 */
	public static getError(error?: any): string {
		let outgoingErrorMessage = this._defaultErrorMessage;

		// Case 1: Error is a custom ExceptionForDisplay type from the API for passing error messages straight through to the UI
		if (error?.name === "AxiosError") {
			const rawMessage = this.getMessage(error);
			if (rawMessage?.includes("ExceptionForDisplay")) {
				const startIndex = rawMessage.indexOf(this._tag.start) + this._tag.start.length;
				const endIndex = rawMessage.indexOf(this._tag.end);
				const passthroughMessage = rawMessage.substring(startIndex, endIndex);
				if (passthroughMessage) {
					outgoingErrorMessage = passthroughMessage;
				}
			}
		}

		// Case X: (Put other error message detecting logic that overwrites outgoingErrorMessage here)
		return outgoingErrorMessage;
	}

	/**
	 * Returns properties about the error.
	 * @param error The error from a catch clause.
	 * @returns An ErrorProperties object.
	 */
	public static getErrorProperties(error?: any) {
		const propertries: ErrorProperties = {
			isSqlTimeout: false
		};
		if (!error) return propertries;

		const rawMessge = this.getMessage(error);
		if (rawMessge.includes("Microsoft.Data.SqlClient.SqlException") && rawMessge.includes("Execution Timeout Expired")) {
			propertries.isSqlTimeout = true;
		}

		return propertries;
	}

	/**
	 * Returns the message of an error, or undefined if not available.
	 * @param error The error from a catch clause.
	 * @returns The error message, or undefined if not available.
	 */
	public static getMessage(error?: any): string {
		if (error?.name === "AxiosError") {
			if (error.status?.toString() === "500") {
				return error.response.data.detail.toString();
			}
			else {
				return error.response?.data?.toString() ?? this._defaultErrorMessage;
			}
		}

		return "";
	}
};